type Format = 'short' | 'long'

const decimalFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'decimal',
  maximumFractionDigits: 2
})

const sizes = {
  short: ['B', 'KB', 'MB', 'GB', 'TB', 'PB'],
  long: ['Byte', 'Kilobyte', 'Megabyte', 'Gigabyte', 'Terabyte', 'Petabyte']
} as const

function pluralize({ value, word, format }: { value: string; word: string; format: Format }): string {
  return format === 'short' || (value && value.length === 1 && Number(value) === 1) ? word : `${word}s`
}

/**
 * Converts a byte value into a human-readable string using short or long formats.
 *
 * @param bytes - The number of bytes to format.
 * @param format - Output format: `'short'` (e.g., "KB") or `'long'` (e.g., "Kilobyte"). Default is `'short'`.
 * @returns A formatted string representing the byte value.
 *
 * @example
 * ```ts
 * toReadableBytes(1024); // '1 KB'
 * toReadableBytes(1024 * 1024, 'long'); // '1 Megabyte'
 * toReadableBytes(1000000); // '976,56 KB'
 * toReadableBytes(-1); // '0 B'
 * toReadableBytes(Number.MAX_SAFE_INTEGER + 100, 'long'); // '> 8 Petabytes'
 * ```
 */
export function toReadableBytes(bytes: number, format: Format = 'short'): string {
  if (isNaN(bytes) || bytes <= 0) return format === 'short' ? '0 B' : '0 Bytes'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))

  const hasUnsafeValue = bytes > Number.MAX_SAFE_INTEGER
  const safeValue = hasUnsafeValue ? Number.MAX_SAFE_INTEGER : bytes

  const value = safeValue / Math.pow(1024, i)
  const formattedValue = decimalFormatter.format(value)
  const suffix = pluralize({
    value: formattedValue,
    word: sizes[format][i],
    format
  })

  return `${hasUnsafeValue ? '> ' : ''}${formattedValue} ${suffix}`
}
